//vuetify doesn't expose its validation types yet so I grabbed them from their github.This may need to change to stay in sync with updates.
export type ValidationResult = string | boolean;
export type ValidationRule =
  | ValidationResult
  | PromiseLike<ValidationResult>
  | ((value: any) => ValidationResult)
  | ((value: any) => PromiseLike<ValidationResult>);

function _reportError(signature: unknown) {
  console.error("Validation rule error: " + signature + " has missing parameter(s)");
  return [() => false || "Validation rule error"];
}

export const Rules = {
  accepted: (v: string | number | boolean): ValidationResult =>
    ["yes", "on", 1, true, "1", "true"].includes(v) || "Field must be accepted",
  afterDate: (date = new Date()) => {
    return (v: string): ValidationResult =>
      new Date(v) > date ||
      "Date must be equal to or after " +
        date.getFullYear() +
        "-" +
        date.getMonth() +
        "-" +
        date.getDate();
  },
  afterOrEqualDate: (date = new Date()) => {
    return (v: string): ValidationResult =>
      new Date(v) >= date ||
      "Date must be equal to or after " +
        date.getFullYear() +
        "-" +
        date.getMonth() +
        "-" +
        date.getDate();
  },
  alpha: (v: string): ValidationResult => /[a-zA-Z]+$/.test(v) || "Field must only contain letters",
  alphaDash: (v: string): ValidationResult =>
    /^[a-zA-Z-_]+$/.test(v) || "Field must only contain letters, hyphens, and underscores",
  alphaNumeric: (v: string): ValidationResult =>
    /^[a-zA-Z0-9]+$/.test(v) || "Field must only contain letters and numbers",
  alphaNumericDash: (v: string): ValidationResult =>
    /^[a-zA-Z0-9-_]+$/.test(v) ||
    "Field must only contain letters, numbers, hyphens, and underscores",
  array: (v: []): ValidationResult => Array.isArray(v) || "Field must be an array",
  beforeDate: (date = new Date()) => {
    return (v: string): ValidationResult =>
      new Date(v) < date ||
      "Date must be before " + date.getFullYear() + "-" + date.getMonth() + "-" + date.getDate();
  },
  beforeOrEqualDate: (date = new Date()) => {
    return (v: string): ValidationResult =>
      new Date(v) <= date ||
      "Date must be equal to or before " +
        date.getFullYear() +
        "-" +
        date.getMonth() +
        "-" +
        date.getDate();
  },
  between: (min: number, max: number) => {
    if (!min || !max) {
      return _reportError("between(min, max)");
    }
    return (v: number): ValidationResult =>
      (v >= min && v <= max) || "Field must be between " + min + " and " + max;
  },
  boolean: (v: boolean): ValidationResult =>
    typeof v === "boolean" || "Field must be true or false",
  dateEquals: (date = new Date()) => {
    return (v: string): ValidationResult =>
      new Date(v) == date ||
      "Date must be equal to or before " +
        date.getFullYear() +
        "-" +
        date.getMonth() +
        "-" +
        date.getDate();
  },
  digits: (length = false) => {
    if (!length) {
      return _reportError("digits(length)");
    }
    return (v: string): ValidationResult => new RegExp("^[0-9]{" + length + "}$").test(v);
  },
  digitsBetween: (minLength = 0, maxLength = false) => {
    if (!maxLength) {
      return _reportError("digitsBetween(minLength, maxLength)");
    }
    return (v: string): ValidationResult =>
      new RegExp("^[0-9]{" + minLength + "," + maxLength + "}$").test(v) ||
      "Field must be numeric and between " + minLength + " and " + maxLength + " long";
  },
  domain: (v: string): ValidationResult =>
    /^(((?!-))(xn--|_{1,1})?[a-z0-9-]{0,61}[a-z0-9]{1,1}\.)*(xn--)?([a-z0-9][a-z0-9\\-]{0,60}|[a-z0-9-]{1,30}\.[a-z]{2,})$/.test(
      v
    ) || "Must be a valid domain name",
  email: [
    (v: string): ValidationResult =>
      !v || /^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/.test(v) || "E-mail must be valid",
    (v: string): ValidationResult => (v || "").indexOf(" ") < 0 || "No spaces are allowed",
  ],
  filled: (v: string): ValidationResult => v != "" || "Field must not be empty",
  gt: (num: number) => {
    return (v: number): ValidationResult => v > num || "Field must be greater than " + num;
  },
  gte: (num: number) => {
    return (v: number): ValidationResult =>
      v >= num || "Field must be greater than or equal to " + num;
  },
  in: (array: []) => {
    return (v: never): ValidationResult => array.indexOf(v) >= 0 || "Field must be a valid option";
  },
  integer: (v: string): ValidationResult =>
    !!(isNaN(parseFloat(v)) ? false : parseFloat(v) || 0 === parseFloat(v)) ||
    "Field must be an integer",
  ipv4: (v: string): ValidationResult =>
    new RegExp(
      "^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$"
    ).test(v) || "Field must be a valid IPv4 Address",
  ipv6: (v: string): ValidationResult =>
    new RegExp(
      "^\\s*((([0-9A-Fa-f]{1,4}:){7}([0-9A-Fa-f]{1,4}|:))|(([0-9A-Fa-f]{1,4}:){6}(:[0-9A-Fa-f]{1,4}|((25[0-5]|2[0-4]\\d|1\\d\\d|[1-9]?\\d)(\\.(25[0-5]|2[0-4]\\d|1\\d\\d|[1-9]?\\d)){3})|:))|(([0-9A-Fa-f]{1,4}:){5}(((:[0-9A-Fa-f]{1,4}){1,2})|:((25[0-5]|2[0-4]\\d|1\\d\\d|[1-9]?\\d)(\\.(25[0-5]|2[0-4]\\d|1\\d\\d|[1-9]?\\d)){3})|:))|(([0-9A-Fa-f]{1,4}:){4}(((:[0-9A-Fa-f]{1,4}){1,3})|((:[0-9A-Fa-f]{1,4})?:((25[0-5]|2[0-4]\\d|1\\d\\d|[1-9]?\\d)(\\.(25[0-5]|2[0-4]\\d|1\\d\\d|[1-9]?\\d)){3}))|:))|(([0-9A-Fa-f]{1,4}:){3}(((:[0-9A-Fa-f]{1,4}){1,4})|((:[0-9A-Fa-f]{1,4}){0,2}:((25[0-5]|2[0-4]\\d|1\\d\\d|[1-9]?\\d)(\\.(25[0-5]|2[0-4]\\d|1\\d\\d|[1-9]?\\d)){3}))|:))|(([0-9A-Fa-f]{1,4}:){2}(((:[0-9A-Fa-f]{1,4}){1,5})|((:[0-9A-Fa-f]{1,4}){0,3}:((25[0-5]|2[0-4]\\d|1\\d\\d|[1-9]?\\d)(\\.(25[0-5]|2[0-4]\\d|1\\d\\d|[1-9]?\\d)){3}))|:))|(([0-9A-Fa-f]{1,4}:){1}(((:[0-9A-Fa-f]{1,4}){1,6})|((:[0-9A-Fa-f]{1,4}){0,4}:((25[0-5]|2[0-4]\\d|1\\d\\d|[1-9]?\\d)(\\.(25[0-5]|2[0-4]\\d|1\\d\\d|[1-9]?\\d)){3}))|:))|(:(((:[0-9A-Fa-f]{1,4}){1,7})|((:[0-9A-Fa-f]{1,4}){0,5}:((25[0-5]|2[0-4]\\d|1\\d\\d|[1-9]?\\d)(\\.(25[0-5]|2[0-4]\\d|1\\d\\d|[1-9]?\\d)){3}))|:)))(%.+)?\\s*$"
    ).test(v) || "Field must be a valid IPv6 Address",
  lt: (num: number) => {
    return (v: number): ValidationResult => v < num || "Field must be less than " + num;
  },
  lte: (num: number) => {
    return (v: number): ValidationResult =>
      v <= num || "Field must be less than or equal to " + num;
  },
  max: (model: unknown, num: number | boolean = false): ValidationResult => {
    let test = false;
    if (typeof model == "string" || Array.isArray(model)) {
      test = (model as any).length <= num;
      return test || "Field must be shorter than " + num;
    } else if (typeof model == "number") {
      test = (model as any) <= num;
      return test || "Field must be less than " + num;
    }
    return test || "Invalid type passed in field";
  },
  min: (model: unknown, num: number | boolean = false) => {
    if (!num) {
      return _reportError("min(num)");
    }
    let test = false;
    if (typeof model == "string" || Array.isArray(model)) {
      test = (model as any).length >= num;
      return (): ValidationResult => test || "Field must be longer than " + num;
    } else if (typeof model == "number") {
      test = (model as any) >= num;
      return (): ValidationResult => test || "Field must be more than " + num;
    }
    return (): ValidationResult => test || "Invalid type passed in field";
  },
  noSpaces: (v: string): ValidationResult => (v || "").indexOf(" ") < 0 || "No spaces are allowed",
  notIn: (array: Array<unknown>) => {
    return (v: unknown): ValidationResult =>
      array.indexOf(v) == -1 || "Field must be a valid option";
  },
  notRegex: (regex: string) => {
    return (v: string): ValidationResult =>
      !new RegExp(regex).test(v) || "Field must be a valid value";
  },
  numeric: (v: string): ValidationResult => /^[0-9]+$/.test(v) || "Field must be numeric",
  phone: (v: string): ValidationResult =>
    /^(\+?)[0-9]{11,13}$/.test(v) || "Between 11-13 digits required (+, 0-9)",
  postcode: [
    (v: string): ValidationResult =>
      (v || "").length <= 5 || "Postcode must be between 3-7 digits long with no spaces",
    (v: string): ValidationResult =>
      (v || "").length >= 7 || "Postcode must be between 3-7 digits long with no spaces",
  ],
  regex: (regex: string) => {
    return (v: string): ValidationResult =>
      new RegExp(regex).test(v) || "Field must be a valid value";
  },
  required: (v: unknown): ValidationResult => !!v || "This field is required",
  requiredNamed: (fieldName: string) => {
    return (v: unknown): ValidationResult =>
      !!v || (fieldName ? fieldName : "This field") + " is required";
  },
  string: (v: string): ValidationResult => typeof v === "string" || "Field must be a string",
  url: (v: string): ValidationResult =>
    /^(((?!-))(xn--|_{1,1})?[a-z0-9-]{0,61}[a-z0-9]{1,1}\.)*(xn--)?([a-z0-9][a-z0-9\\-]{0,60}|[a-z0-9-]{1,30}\.[a-z]{2,})$/.test(
      v
    ) || "Field must be a valid URL",
  password: (v: string): ValidationResult => {
    let points = 0;

    if (v == null || v.length == 0) {
      return "This field is required";
    }

    if (v.length < 8) {
      return "Password length must a minimum of 8 characters.";
    }

    if (/[a-z]/.test(v)) {
      points++;
    }

    if (/[A-Z]/.test(v)) {
      points++;
    }

    if (/[0-9]/.test(v)) {
      points++;
    }

    if (/[~!@#$%^&*()_+=-`]/.test(v)) {
      points++;
    }

    if (points < 3) {
      return "Password must contain at least 3 of the following symbol, number, uppercase letter or a lowercase letter";
    }

    return true;
  },
  decimalString: (v: string): ValidationResult =>
    /^-?(\d{1,3})(,\d{3})*(\.\d+)?$/.test(v) || "Field must be decimal",
  bankRoutingNumberValid: (value: string): ValidationResult => {
    if (!value || /^\d{9}$/.test(value) == false) {
      return "Field must be a valid U.S. 9-digit bank routing number";
    }

    // Run through each digit and calculate the total.
    let n = 0;
    for (let i = 0; i < (value as string).length; i += 3) {
      n +=
        parseInt((value as string).charAt(i), 10) * 3 +
        parseInt((value as string).charAt(i + 1), 10) * 7 +
        parseInt((value as string).charAt(i + 2), 10);
    }

    // If the resulting sum is an even multiple of ten (but not zero),
    // the aba routing number is good.
    if (n != 0 && n % 10 == 0) {
      return true;
    } else {
      return "Field must be a valid U.S. 9-digit bank routing number";
    }
  },
  isCreditCardOfType: (value:string, acceptedTypes: string[]): ValidationResult => {

    // const acceptedTypes = [
    //   "visaelectron",
    //   "visa",
    //   "elo",
    //   "amex",
    //   "mastercard",
    //   "discover",
    //   "unionpay",
    //   "troy",
    //   "dinersclub",
    //   "jcb",
    //   "laser",
    //   "dankort",
    //   "uatp",
    //   "mir",
    //   "hipercard",
    //   "aura",
    //   "maestro",
    // ];

    const number = value.replace(/\s+/g, "");

    if (number.match(/^4(026|17500|405|508|844|91[37])/)) {
      if (acceptedTypes.indexOf("visaelectron") >= 0){
        return true;
      }
      return "We Cannot accept Visa Electron Cards at this time.";
    }
    if (number.match(/^4\d{12}(\d{3})?$/)){
      if (acceptedTypes.indexOf("visa") >= 0){
        return true;
      }
      return "We Cannot accept Visa Cards at this time.";
    }
    if (
      number.match(
        /^((509091)|(636368)|(636297)|(504175)|(438935)|(40117[8-9])|(45763[1-2])|(457393)|(431274)|(50990[0-2])|(5099[7-9][0-9])|(50996[4-9])|(509[1-8][0-9][0-9])|(5090(0[0-2]|0[4-9]|1[2-9]|[24589][0-9]|3[1-9]|6[0-46-9]|7[0-24-9]))|(5067(0[0-24-8]|1[0-24-9]|2[014-9]|3[0-379]|4[0-9]|5[0-3]|6[0-5]|7[0-8]))|(6504(0[5-9]|1[0-9]|2[0-9]|3[0-9]))|(6504(8[5-9]|9[0-9])|6505(0[0-9]|1[0-9]|2[0-9]|3[0-8]))|(6505(4[1-9]|5[0-9]|6[0-9]|7[0-9]|8[0-9]|9[0-8]))|(6507(0[0-9]|1[0-8]))|(65072[0-7])|(6509(0[1-9]|1[0-9]|20))|(6516(5[2-9]|6[0-9]|7[0-9]))|(6550(0[0-9]|1[0-9]))|(6550(2[1-9]|3[0-9]|4[0-9]|5[0-8])))/
      )
    ) {
      if (acceptedTypes.indexOf("elo") >= 0){
        return true;
      }
      return "We Cannot accept Elo Cards at this time.";
    }
    if (number.match(/^3[47]\d{13}$/)){
      if (acceptedTypes.indexOf("amex") >= 0){
        return true;
      }
      return "We Cannot accept American Express Cards at this time.";
    }
    if (number.match(/^(5[1-5]\d{4}|677189)\d{10}$/)){
      if (acceptedTypes.indexOf("mastercard") >= 0){
        return true;
      }
      return "We Cannot accept Mastercard Cards at this time.";
    }
    if (number.match(/^6(?:011|5[0-9]{2})[0-9]{12}$/)){
      if (acceptedTypes.indexOf("discover") >= 0){
        return true;
      }
      return "We Cannot accept Discover Cards at this time.";
    }
    if (number.match(/^62[0-9]\d{14,17}$/)){
      if (acceptedTypes.indexOf("unionpay") >= 0){
        return true;
      }
      return "We Cannot accept UnionPay Cards at this time.";
    }
    if (number.match(/^9792\d{12}$/)){
      if (acceptedTypes.indexOf("troy") >= 0){
        return true;
      }
      return "We Cannot accept Troy Cards at this time.";
    }
    if (number.match(/^3(0[0-5]|[68]\d)\d{11,16}/)){
      if (acceptedTypes.indexOf("dinersclub") >= 0){
        return true;
      }
      return "We Cannot accept Diners Club Cards at this time.";
    }
    if (number.match(/(?:2131|1800|35[0-9]{3})[0-9]{11}$/)){
      if (acceptedTypes.indexOf("jcb") >= 0){
        return true;
      }
      return "We Cannot accept JCB Cards at this time.";
    }
    if (number.match(/^(6304|6706|6709|6771)[0-9]{12,15}$/)){
      if (acceptedTypes.indexOf("laser") >= 0){
        return true;
      }
      return "We Cannot accept Laser Cards at this time.";
    }
    if (number.match(/^5019\d{12}$/)){
      if (acceptedTypes.indexOf("dankort") >= 0){
        return true;
      }
      return "We Cannot accept Dankort Cards at this time.";
    }
    if (number.match(/^1\d{14}$/)){
      if (acceptedTypes.indexOf("uatp") >= 0){
        return true;
      }
      return "We Cannot accept UATP Cards at this time.";
    }
    if (number.match(/^220[0-4]\d{12}$/)){
      if (acceptedTypes.indexOf("mir") >= 0){
        return true;
      }
      return "We Cannot accept Mir Cards at this time.";
    }
    if (number.match(/^(606282\d{10}(\d{3})?)|(3841\d{15})$/)) {
      if (acceptedTypes.indexOf("hipercard") >= 0){
        return true;
      }
      return "We Cannot accept Hipercard Cards at this time.";
    }
    if (number.match(/^((?!504175))^((?!5067))(^50[0-9])/)){
      if (acceptedTypes.indexOf("aura") >= 0){
        return true;
      }
      return "We Cannot accept Aura Cards at this time.";
    }
    if (number.match(/(?:5[0678]\d\d|6304|6390|67\d\d)\d{8,15}$/)) {
      if (acceptedTypes.indexOf("maestro") >= 0){
        return true;
      }
      return "We Cannot accept Maestro Cards at this time.";
    }


    return "We Cannot accept this Card Type at this time.";
  },
  creditCardExpiry: (value: string): ValidationResult => {
    if (!/^\d{2}\/\d{2}$/.test(value)) {
      return 'Format must be MM/YY.';
    }

    const [monthStr, yearStr] = value.split('/');
    const month = parseInt(monthStr, 10);
    const year = parseInt(yearStr, 10);

    if (month < 1 || month > 12) {
      return 'Invalid month (01-12).';
    }

    const currentYear = new Date().getFullYear() % 100;
    const currentMonth = new Date().getMonth() + 1;

    if (year < currentYear) {
      return 'Card has expired.';
    }
    if (year === currentYear && month < currentMonth) {
      return 'Card has expired.';
    }

    return true;
  },
  creditCardCVV: (value: string): ValidationResult => {
    const val = value.replace(/\D/g, '');
    if (!/^\d{3,4}$/.test(val)) {
      return 'CVV must be 3 or 4 digits.';
    }
    return true;
  },
};
